<script setup>
import { useI18n } from 'vue-i18n'
import Header from '@/components/Header'
import DesignFooter from '@/components/DesignFooter.vue'
import { childItem } from './contant.js'

const { t } = useI18n()
const robrain = childItem.Robrain
const asset = (path) => `${import.meta.env.BASE_URL}${path}`
const heroImage = asset('image/solution-redesign/robrain-banner.png')
const painPoints = [
  { title: '远场距离拾音，语音交互效果差', icon: 'demo1' },
  { title: '语音控制延时大，响应速度慢', icon: 'delay' },
  { title: '单一语音，缺乏更自然的多模态交互', icon: 'single' },
]
const overviewBlocks = [
  { key: 'first', translation: robrain.pectorophonyScheme, images: robrain.pectorophonyImg, alt: 'Robrain AI机器人解决方案架构' },
  { key: 'second', html: robrain.pectorophonyScheme2, images: robrain.pectorophonyImg2, alt: 'Robrain 多模态交互与机器人动作控制' },
  { key: 'third', html: robrain.pectorophonyScheme3, images: robrain.pectorophonyImg3, alt: 'Robrain 智能听觉与声纹识别系统' },
]
const sceneTitles = ['教育互动', '零售导购']
</script>

<template>
  <Header design-always hide-banner />

  <main class="robrain-page">
    <section class="robrain-hero" aria-labelledby="robrain-title">
      <img :src="heroImage" class="hero-image" alt="" fetchpriority="high" />
      <div class="robrain-container hero-content">
        <p class="hero-breadcrumb">解决方案 / Robrain AI机器人解决方案</p>
        <h1 id="robrain-title">{{ t(robrain.name) }}</h1>
        <p class="hero-description">端云混合大模型语音交互方案，让体验更好，决策更加智能</p>
      </div>
    </section>

    <section class="robrain-section section-muted" aria-labelledby="robrain-background-title">
      <div class="robrain-container">
        <h2 id="robrain-background-title">业务背景</h2>
        <p class="background-copy">{{ t(robrain.businessBg) }}</p>
      </div>
    </section>

    <section class="robrain-section" aria-labelledby="robrain-pain-title">
      <div class="robrain-container">
        <h2 id="robrain-pain-title">行业痛点</h2>
        <div class="pain-grid">
          <article v-for="point in painPoints" :key="point.icon" class="pain-card">
            <span class="pain-icon" aria-hidden="true">
              <img :src="asset(`svg/${point.icon}.svg`)" alt="" width="24" height="24" loading="lazy" />
            </span>
            <h3>{{ point.title }}</h3>
          </article>
        </div>
      </div>
    </section>

    <section class="robrain-section section-muted" aria-labelledby="robrain-overview-title">
      <div class="robrain-container">
        <h2 id="robrain-overview-title">方案概述</h2>
        <div class="overview-stack">
          <template v-for="block in overviewBlocks" :key="block.key">
            <!-- 仅渲染仓库内可信静态文案；保留原 HTML 和正文顺序。 -->
            <div class="rich-copy" v-html="block.translation ? t(block.translation) : block.html" />
            <img
              v-for="image in block.images"
              :key="image"
              :src="image"
              :alt="block.alt"
              class="overview-image"
              loading="lazy"
              decoding="async"
            />
          </template>
        </div>
      </div>
    </section>

    <section class="robrain-section" aria-labelledby="robrain-scenes-title">
      <div class="robrain-container">
        <h2 id="robrain-scenes-title">应用场景</h2>
        <p class="section-description">按使用场景和设备条件选择能力</p>
        <div class="scene-grid">
          <article v-for="(scene, index) in robrain.pectorophonyApply" :key="scene.name" class="scene-card">
            <img :src="scene.img" :alt="sceneTitles[index]" class="scene-image" loading="lazy" decoding="async" />
            <div class="scene-body">
              <h3>{{ sceneTitles[index] }}</h3>
              <p>{{ scene.dec }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="robrain-section section-muted" aria-labelledby="robrain-cases-title">
      <div class="robrain-container">
        <h2 id="robrain-cases-title">相关案例</h2>
        <div class="case-grid">
          <a v-for="item in robrain.caseImg" :key="item.link" :href="item.link" class="case-card" v-track="{ position: 'solution > case', page: 'robrain', case: item.title || item.name || item.link.split('?')[0] }" target="_blank" rel="noopener noreferrer">
            <img :src="item.url" alt="" class="case-image" loading="lazy" decoding="async" />
            <div class="case-body">{{ item.dec }}</div>
          </a>
        </div>
      </div>
    </section>
  </main>

  <DesignFooter />
</template>

<style scoped>
.robrain-page { min-width: 0; color: #14233b; background: #fff; }
.robrain-page, .robrain-page *, .robrain-page *::before, .robrain-page *::after { box-sizing: border-box; }
.robrain-container { width: min(1440px, calc(100% - 48px)); margin-inline: auto; }
.robrain-page h1, .robrain-page h2, .robrain-page h3, .robrain-page p { margin: 0; }
.robrain-page h2 { margin-bottom: 24px; color: #14233b; font-size: 24px; font-weight: 500; line-height: 1.4; text-align: center; }
.robrain-section { padding-block: 64px; }
.section-muted { background: #f4f7fb; }
.robrain-hero { position: relative; display: flex; align-items: center; min-height: 352px; padding-block: 64px; color: #fff; background: #0d182b; }
.hero-image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.hero-content { position: relative; }
.hero-breadcrumb { margin-bottom: 8px !important; font-size: 14px; line-height: 24px; }
.hero-content h1 { margin-bottom: 16px; color: #fff; font-size: 30px; font-weight: 600; line-height: 1.35; overflow-wrap: anywhere; }
.hero-description { font-size: 16px; line-height: 1.5; }
.background-copy { color: #617089; font-size: 15px; line-height: 29px; text-align: center; }
.pain-grid, .scene-grid, .case-grid { display: grid; gap: 24px; align-items: start; }
.pain-grid, .scene-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.scene-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); width: calc((100% - 48px) / 3 * 2 + 24px); margin-inline: auto; }
.case-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); align-items: stretch; }
.pain-card, .scene-card, .case-card { min-width: 0; border: 1px solid #dce2ea; border-radius: 16px; background: #fff; }
.pain-card { display: flex; align-items: center; gap: 16px; padding: 24px; height: 100%; }
.pain-icon { display: flex; flex: 0 0 40px; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 50%; background: #f4f7fb; }
.pain-icon img { display: block; width: 24px; height: 24px; }
.pain-card h3, .scene-body h3 { color: #14233b; font-size: 18px; font-weight: 500; line-height: 1.5; }
.overview-stack { display: flex; flex-direction: column; gap: 24px; }
.rich-copy { color: #617089; font-size: 15px; line-height: 29px; overflow-wrap: break-word; }
.rich-copy :deep(h3) { margin: 0; color: #14233b !important; font-size: 22px; font-weight: 500; line-height: 1.5; }
.rich-copy :deep(b) { color: #14233b !important; font-weight: 500; }
.overview-image { display: block; width: 100%; height: auto; border-radius: 16px; }
.section-description { margin: -8px 0 24px !important; color: #617089; font-size: 15px; line-height: 29px; text-align: center; }
.scene-card { overflow: hidden; }
.scene-image { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: cover; }
.scene-body { padding: 24px; }
.scene-body h3 { margin-bottom: 8px; }
.scene-body p { color: #617089; font-size: 14px; line-height: 24px; }
.case-card { display: block; overflow: hidden; color: #14233b; text-decoration: none; transition: border-color 160ms ease, box-shadow 160ms ease; }
.case-image { display: block; width: 100%; aspect-ratio: 342 / 146; object-fit: contain; background: #fff; }
.case-body { padding: 24px; font-size: 16px; line-height: 24px; }
.case-card:hover { color: #14233b; border-color: #9baabd; box-shadow: 0 8px 24px rgb(20 35 59 / 8%); }
.case-card:focus-visible { outline: 3px solid #1478d4; outline-offset: 4px; }
@media (min-width: 768px) {
  .robrain-page h2 { font-size: 26px; }
  .hero-content h1 { font-size: 36px; }
  .hero-description { font-size: 18px; }
}
@media (min-width: 992px) {
  .robrain-page h2 { font-size: 28px; }
  .hero-content h1 { font-size: 40px; }
  .hero-description { font-size: 18px; }
}
@media (min-width: 1200px) {
  .robrain-page h2 { font-size: 30px; }
  .hero-content h1 { font-size: 44px; }
  .hero-description { font-size: 18px; }
}
@media (min-width: 1400px) {
  .robrain-page h2 { font-size: 32px; }
}
@media (max-width: 991px) {
  .pain-grid, .scene-grid, .case-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .scene-grid { width: 100%; }
}
@media (max-width: 767px) {
  .robrain-container { width: calc(100% - 32px); }
  .robrain-section { padding-block: 40px; }
  .robrain-hero { min-height: 280px; padding-block: 40px; }
  .pain-grid, .scene-grid, .case-grid { grid-template-columns: minmax(0, 1fr); }
  .rich-copy :deep(h3) { font-size: 20px; }
}
@media (prefers-reduced-motion: reduce) {
  .case-card { transition: none; }
}
</style>